import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useApp, API } from "../context/AppContext";
import PlanCard from "../components/PlanCard";
import { Loader2, Users2, Trophy } from "lucide-react";

export default function GroupVote() {
  const { id } = useParams();
  const { t } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voterName, setVoterName] = useState(() => localStorage.getItem("voter_name") || "");
  const [selected, setSelected] = useState(null);
  const [voted, setVoted] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/plans/shared/${id}`);
      setData(data);
    } catch {
      toast.error("404");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const submitVote = async () => {
    if (!voterName.trim() || !selected) return;
    try {
      await axios.post(`${API}/plans/shared/${id}/vote`, {
        voter_name: voterName.trim(),
        plan_id: selected,
      });
      localStorage.setItem("voter_name", voterName.trim());
      setVoted(true);
      toast.success(t.voted);
      load();
    } catch {
      toast.error("Error");
    }
  };

  if (loading) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-[#D95338]" /></div>;
  if (!data) return <div className="max-w-3xl mx-auto p-10 text-center text-stone-600">404</div>;

  const tally = data.tally || {};
  const totalVotes = Object.values(tally).reduce((a, b) => a + b, 0);
  const winnerId = Object.entries(tally).sort((a, b) => b[1] - a[1])[0]?.[0];

  return (
    <div className="max-w-4xl mx-auto px-5 py-8">
      <div className="rounded-3xl bg-white border border-[#E7E0D5] p-6 mb-6">
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-mono font-semibold text-stone-500">
          <Users2 className="w-3.5 h-3.5" />{t.groupVote}
        </span>
        <h1 className="mt-2 text-2xl sm:text-3xl font-bold text-stone-900">{t.voteFor}</h1>
        <p className="text-sm text-stone-600 mt-1">
          {totalVotes} {t.votesCount(totalVotes)}
        </p>
        {!voted && (
          <div className="mt-4 flex flex-wrap gap-3 items-end">
            <label className="flex flex-col gap-1.5 flex-1 min-w-[180px]">
              <span className="text-xs uppercase font-mono text-stone-500">{t.voterName}</span>
              <input data-testid="input-voter-name" value={voterName} onChange={(e) => setVoterName(e.target.value)} className="input-base" />
            </label>
            <button data-testid="button-submit-vote" onClick={submitVote} disabled={!voterName.trim() || !selected}
              className="px-6 py-3 rounded-full bg-[#D95338] hover:bg-[#C24127] text-white font-semibold disabled:opacity-40 active:scale-95 transition-all">
              {t.vote}
            </button>
          </div>
        )}
      </div>

      <div className="grid gap-4">
        {data.plans.map((plan, i) => {
          const votes = tally[plan.id] || 0;
          const isWinner = plan.id === winnerId && votes > 0;
          const isSelected = selected === plan.id;
          return (
            <div key={plan.id} className={`relative rounded-3xl transition-all ${isSelected ? "ring-2 ring-[#D95338] ring-offset-2 ring-offset-[#FAF7F2]" : ""}`}>
              <button
                type="button"
                onClick={() => !voted && setSelected(plan.id)}
                data-testid={`select-plan-${i}`}
                className="w-full text-left"
                disabled={voted}
              >
                <PlanCard plan={plan} index={i} canSave={false} />
              </button>
              <div className="absolute top-5 right-5 flex items-center gap-2">
                {isWinner && (
                  <span className="inline-flex items-center gap-1 text-xs font-bold bg-[#E09F3E] text-stone-900 px-2.5 py-1 rounded-full">
                    <Trophy className="w-3.5 h-3.5" />{t.groupWinner}
                  </span>
                )}
                <span className="text-sm font-bold bg-white border border-[#E7E0D5] px-3 py-1 rounded-full">
                  {votes} 🗳️
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
